<template>
  <div
    class="rf-left-nav-bar bg-black-1 fixed top-0.5 left-0 flex h-full flex-col pt-12!"
    data-cy="main-navigation"
  >
    <div
      class="mx-auto! grow-0 px-2! py-2"
      :class="{ '2xl:mx-0! 2xl:px-5! 2xl:py-4': wideSidebar }"
    >
      <RfMenu
        class="rf-menu [&_.rf-menu--icon-wrapper_.rf-icon]:fill-white-1 w-full"
        :preset="wideSidebar ? null : 'appSelectorSmall'"
        :hideIcon="!wideSidebar"
        :sameWidth="wideSidebar"
        :class="{ 'rf-menu--background': wideSidebar }"
      >
        <template #activator>
          <span
            data-cy="rf-app-selector--activator"
            :class="{ '2xl:text-body': wideSidebar }"
            class="text-heading-0 text-white-1 w-full"
          >
            <span :class="{ '2xl:flex': wideSidebar }" class="hidden">
              {{ currApp?.name || "App" }}
            </span>
            <span
              :class="{ '2xl:hidden': wideSidebar }"
              class="inline-grid aspect-square w-10 shrink-0 place-items-center rounded font-normal!"
              :style="`background-color: ${getAppColor(currApp.id)}`"
            >
              {{ currApp.name[0] || "A" }}
            </span>
          </span>
        </template>
        <template #menu="{ close }">
          <div
            data-cy="rf-app-selector--list"
            class="list flex min-w-36 shrink grow-0 flex-col gap-2 p-4"
          >
            <template v-for="app in apps" :key="app?.id">
              <button
                class="flex items-center gap-5 rounded p-1"
                @click="(emit('switchApp', app), close())"
              >
                <span
                  class="text-heading-0 text-white-1 inline-grid aspect-square w-10 shrink-0 place-items-center rounded font-normal!"
                  :style="`background-color: ${getAppColor(app.id)}`"
                >
                  {{ app.name[0] }}
                </span>
                <span class="text-left">
                  {{ app.name }}
                </span>
              </button>
            </template>
            <RouterLink
              v-if="userStore.isCurrentSuperAdmin"
              class="flex items-center gap-5 rounded p-1"
              to="/newapp"
              @click="close"
            >
              <v-icon size="40" color="primary">md:add_box</v-icon>Add New App
            </RouterLink>
          </div>
        </template>
      </RfMenu>
    </div>
    <div class="flex h-full flex-col overflow-auto">
      <RfLeftMainNavLink
        label="Dashboard"
        :wideSidebar="wideSidebar"
        :linkTo="{ path: `/apps/${currApp.id}/dashboard` }"
        :style="deactivateLink('dashboard')"
        :icon="RfSpeedIcon"
      />
      <RfLeftMainNavLink
        label="Segments"
        :wideSidebar="wideSidebar"
        :linkClasses="activeClass('segments')"
        :linkTo="{ path: `/apps/${currApp.id}/segments` }"
        :style="deactivateLink('segments', true)"
        :icon="RfPieChartIcon"
      />
      <RfLeftMainNavLink
        v-if="currApp.feature_set.includes('sequences')"
        label="Guides"
        :wideSidebar="wideSidebar"
        :linkClasses="activeClass('sequences')"
        :linkTo="{ path: `/apps/${currApp.id}/experiences` }"
        :style="deactivateLink('sequences', true)"
        :icon="RfArrowSplitIcon"
      />
      <RfLeftMainNavLink
        label="Prompts"
        :wideSidebar="wideSidebar"
        :linkClasses="activeClass('prompts')"
        :linkTo="{ path: `/apps/${currApp.id}/prompts` }"
        :style="deactivateLink('prompts', true)"
        :icon="RfCallToActionIcon"
      />
      <RfLeftMainNavLink
        label="Live"
        :wideSidebar="wideSidebar"
        :linkClasses="activeClass('live')"
        :linkTo="{ path: `/apps/${currApp.id}/live` }"
        :style="deactivateLink('live', true)"
        :icon="RfBoltOutlinedIcon"
      />
      <RfLeftMainNavLink
        v-if="currApp.flags.ai_agent_guide_id"
        label="Agent"
        :wideSidebar="wideSidebar"
        :linkClasses="activeClass('agent')"
        :linkTo="{ path: `/apps/${currApp.id}/agent/${currApp.flags.ai_agent_guide_id}` }"
        :style="deactivateLink('agent')"
        :icon="RfRobotIcon"
      />
      <RfLeftMainNavLink
        label="Preview"
        :wideSidebar="wideSidebar"
        :icon="RfVisibilityBoldIcon"
        @click="emit('clickLive')"
      />
      <RfLeftMainNavLink
        label="Settings"
        :wideSidebar="wideSidebar"
        :linkClasses="activeClass('settings')"
        :linkTo="{ path: `/apps/${currApp.id}/settings` }"
        :style="deactivateLink('settings')"
        :icon="RfSettingsIcon"
      />
      <RfLeftMainNavLink
        label="Docs"
        :wideSidebar="wideSidebar"
        :nativeLinkTo="docsUrl"
        :icon="RfBookIcon"
      />
      <RfLeftMainNavLink
        class="mt-auto mb-6 [&_.rf-leftnav-item--icon]:h-6! [&_.rf-leftnav-item--icon]:w-6!"
        label="Log out"
        :wideSidebar="wideSidebar"
        :icon="RfExitIcon"
        @click="userStore.logout()"
      />
    </div>
  </div>
</template>

<script setup>
import RfExitIcon from "@/components/icons/RfExitIcon.vue";
import RfRobotIcon from "@/components/icons/RfRobotIcon.vue";
import RfVisibilityBoldIcon from "@/components/icons/RfVisibilityBoldIcon.vue";
import RfMenu from "@/components/menus/RfMenu.vue";
import { biggerOrEqual2xl } from "@/utils/composables/useBreakpoint";
import { getAppColor } from "@/utils/getAppColors";
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "@/pinia/userStore";
import { RouterLink } from "vue-router";
import RfLeftMainNavLink from "@/blocks/RfLeftMainNav/RfLeftMainNavLink.vue";
import RfSpeedIcon from "@/components/icons/RfSpeedIcon.vue";
import RfCallToActionIcon from "@/components/icons/RfCallToActionIcon.vue";
import RfArrowSplitIcon from "@/components/icons/RfArrowSplitIcon.vue";
import RfBoltOutlinedIcon from "@/components/icons/RfBoltOutlinedIcon.vue";
import RfBookIcon from "@/components/icons/RfBookIcon.vue";
import RfSettingsIcon from "@/components/icons/RfSettingsIcon.vue";
import RfPieChartIcon from "@/components/icons/RfPieChartIcon.vue";

const routesByParent = {
  dashboard: ["App Dashboard"],
  segments: ["All Segments", "Segment Detail"],
  prompts: [
    "All Prompts",
    "Placement Detail",
    "Retention Detail",
    "Retention root",
    "Retention Analysis",
    "Retention Details",
    "Retention Stats",
    "Retention Experiments",
    "Retention History",
  ],
  sequences: ["All Guides", "Guide Detail"],
  settings: ["Settings"],
  agent: ["Agent Detail"],
};

defineProps({
  apps: { type: Array, required: true },
  currApp: { type: Object, required: true },
  docsUrl: { type: String, required: true },
});

const emit = defineEmits(["switchApp", "clickLive"]);

const userStore = useUserStore();
const root = document.querySelector(":root");
const route = useRoute();
const currentRouteName = computed(() => route.name);
const isRouteAllowed = computed(() =>
  [
    "App Dashboard",
    "All Segments",
    "All Prompts",
    "All Guides",
    "Company Settings",
    "Global Settings",
  ].includes(currentRouteName.value),
);
const wideSidebar = computed(() => isRouteAllowed.value && biggerOrEqual2xl.value);

watch(
  wideSidebar,
  v => {
    root.style.setProperty("--left-main-bar-width", v ? "240px" : "60px");
  },
  { immediate: true },
);

const deactivateLink = (page, exact = false) => {
  const routes = routesByParent[page];
  if (exact && routes && routes.indexOf(currentRouteName.value) !== 0) return "";
  if (routes && routes.includes(currentRouteName.value)) return "pointer-events:none;";
  return "";
};
const activeClass = page => {
  const routes = routesByParent[page];
  if (routes && routes.includes(currentRouteName.value)) return "router-link-active";
  return "";
};
</script>

<style lang="scss" scoped>
:deep(.rf-menu.rf-menu--background.rf-menu .rf-menu--button) {
  flex-grow: 1;
  padding: 8px 8px 8px 16px;
  border-radius: 8px;
  background: #146bb2;
}
</style>

<style lang="scss">
.rf-left-nav-bar {
  z-index: 5;
  width: var(--left-main-bar-width, 60px);
  transition: width 100ms ease-in-out;
  padding-top: var(--main-margin-top, 48px);
}
</style>
